"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ActiveOrNaturalSummarizer = void 0;
const getActivitiesDB_1 = require("./utils/getActivitiesDB");
const getPatentsDB_1 = require("./utils/getPatentsDB");
const getPubmedsDB_1 = require("./utils/getPubmedsDB");
const getTaxonomiesDB_1 = require("./utils/getTaxonomiesDB");
const searchActivitiesDB_1 = require("./utils/searchActivitiesDB");
const searchPatentsDB_1 = require("./utils/searchPatentsDB");
const searchPubmedsDB_1 = require("./utils/searchPubmedsDB");
const searchTaxonomiesDB_1 = require("./utils/searchTaxonomiesDB");
const summarizeEmptyTerms_1 = require("./utils/summarizeEmptyTerms");
class ActiveOrNaturalSummarizer {
    /**
     * @description Summarize the active or natural details with the given terms
     * @param {Object} activeOrNaturalDetails The active or natural details to summarize
     * @param {Object} [options={}] The options to use for the search
     * @param {Object} [options.activities={}] The options to use for the activities search
     * @param {number} [options.activities.minScore=0.5] - Minimum score for an entry to be returned
     * @param {number} [options.activities.maxNbEntries=100] - Maximum number of entries to return
     * @param {object} [options.activities.relevance={ k: 1.2, b: 0.75, d: 0.5 }] - BM25 algorithm {k: Term frequency saturation parameter, b: Length normalization parameter, d:Frequency normalization lower bound}
     * @param {number} [options.activities.tolerance=1] -Typo Tolerance following the Levenshtein algorithm
     * @param {string[]} [options.activities.queryFields=['assay']] - Fields to query
     * @param {Object} [options.taxonomies={}] The options to use for the activities search
     * @param {number} [options.taxonomies.minScore=0.5] - Minimum score for an entry to be returned
     * @param {number} [options.taxonomies.maxNbEntries=50] - Maximum number of entries to return
     * @param {number} [options.taxonomies.tolerance=1] -Typo Tolerance following the Levenshtein algorithm
     * @param {object} [options.patents={}] - Options for patents
     * @param {number} [options.patents.abstractsLimit=1000] - If more than this number of abstracts, the search will be done on the without abstracts
     * @param {number} [options.patents.maxNbEntries=100] - Maximum number of entries to return
     * @param {number} [options.patents.inScore=0.5] - Minimum score for an entry to be returned
     * @param {object} [options.patents.relevance={ k: 1.2, b: 0.75, d: 0.5 }] - BM25 algorithm {k: Term frequency saturation parameter, b: Length normalization parameter, d:Frequency normalization lower bound}
     * @param {number} [options.patents.tolerance=1] -Typo Tolerance following the Levenshtein algorithm
     * @param {string[]} [options.patents.queryFields=['title', 'abstract']] - Fields to query
     * @param {object} [options.patents.boostFields={ title: 2, abstract: 1 }] - Fields weights, higher weight means higher importance
     * @param {object} [options.pubmeds={}] - Options for pubmeds
     * @param {number} [options.pubmeds.abstractsLimit=1000] - If more than this number of abstracts, the search will be done on the without abstracts
     * @param {number} [options.pubmeds.minScore=0.5] - Minimum score for an entry to be returned
     * @param {number} [options.pubmeds.maxNbEntries=100] - Maximum number of entries to return
     * @param {object} [options.pubmeds.relevance={ k: 1.2, b: 0.75, d: 0.5 }] - BM25 algorithm {k: Term frequency saturation parameter, b: Length normalization parameter, d:Frequency normalization lower bound}
     * @param {number} [options.pubmeds.tolerance=1] -Typo Tolerance following the Levenshtein algorithm
     * @param {object} [options.pubmeds.boostFields={ title: 2, abstract: 1, meshHeadings: 1 }]  - Fields weights, higher weight means higher importance
     * @param {string[]} [options.pubmeds.queryFields=['title', 'abstract', 'meshHeadings']] - Fields to query
     * @param {(action,current,last)=>{}} [options.callback] - Callback function to call during database creation
     * @returns
     */
    constructor(activeOrNaturalDetails, options = {}) {
        this.options = options;
        this.activeOrNaturalDetails = activeOrNaturalDetails;
        this.activities = activeOrNaturalDetails.data.activities;
        this.patents = activeOrNaturalDetails.data.patents;
        this.pubmeds = activeOrNaturalDetails.data.pubmeds;
        this.taxonomies = activeOrNaturalDetails.data.taxonomies;
        this.isInitialized = false;
    }
    /**
     * @description Summarize the active or natural details with the given terms
     * @param {string} terms The terms to search
     * @returns {Promise<Object>} The summarized active or natural details
     */
    async summarize(terms) {
        if (terms === '') {
            const { patents, pubmeds, activities } = (0, summarizeEmptyTerms_1.summarizeEmptyTerms)(this.activities, this.patents, this.pubmeds, this.taxonomies, this.options);
            return {
                ...this.activeOrNaturalDetails,
                data: {
                    ...this.activeOrNaturalDetails.data,
                    activities,
                    patents,
                    pubmeds,
                },
            };
        }
        if (!this.isInitialized) {
            if (!this.creatingDB) {
                this.creatingDB = this.createDB();
            }
            await this.creatingDB;
            this.isInitialized = true;
            this.creatingDB = undefined;
        }
        return {
            ...this.activeOrNaturalDetails,
            data: {
                ...this.activeOrNaturalDetails.data,
                activities: await (0, searchActivitiesDB_1.searchActivitiesDB)(this.activitiesDB, this.activities, terms, this.options?.activities),
                patents: await (0, searchPatentsDB_1.searchPatentsDB)(this.patentsDB, this.patents, terms, this.options?.patents),
                pubmeds: await (0, searchPubmedsDB_1.searchPubmedsDB)(this.pubmedsDB, this.pubmeds, terms, this.options?.pubmeds),
                taxonomies: await (0, searchTaxonomiesDB_1.searchTaxonomiesDB)(this.taxonomiesDB, this.taxonomies, terms, this.options?.taxonomies),
            },
        };
    }
    async createDB() {
        this.pubmedsDB = await (0, getPubmedsDB_1.getPubmedsDB)(this.pubmeds, {
            callback: this.options.callback,
            ...this.options.pubmeds,
        });
        this.activitiesDB = await (0, getActivitiesDB_1.getActivitiesDB)(this.activities, this.options.activities);
        this.patentsDB = await (0, getPatentsDB_1.getPatentsDB)(this.patents, {
            callback: this.options.callback,
            ...this.options.patents,
        });
        this.taxonomiesDB = await (0, getTaxonomiesDB_1.getTaxonomiesDB)(this.taxonomies, this.options.taxonomies);
    }
}
exports.ActiveOrNaturalSummarizer = ActiveOrNaturalSummarizer;
//# sourceMappingURL=ActiveOrNaturalSummarizer.js.map